import React from 'react'
import { mdx } from '@mdx-js/react'
/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../../components/docs/Layout';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Migrating from Plaid Exchange',
  layout: 'guide',
  description: 'Move an existing Plaid Exchange integration to Core Exchange',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <div className={styles.page}>
      <Header title='Migrating from Plaid Exchange' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "why-migrate"
      }}>{`Why migrate`}</h2>
      <p>{`Plaid Exchange is closed to new integrations; its own documentation directs implementers to Core Exchange, the only specification Plaid continues to develop, covering FDX 4.6 and later. Core Exchange offers self-serve tooling that Plaid Exchange lacks: a Data Partner Dashboard, an in-browser Validator, Permissions Manager, and App Directory.`}</p>
      <h2 {...{
        "id": "whats-different-at-a-glance"
      }}>{`What's different, at a glance`}</h2>
      <Layout.FullWidth>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Area`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Plaid Exchange`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Core Exchange`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Data spec`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Plaid-proprietary schema`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`FDX-aligned (4.6+)`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Endpoints`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`4 endpoints (`}<inlineCode parentName="td">{`/oauth2/auth`}</inlineCode>{`, `}<inlineCode parentName="td">{`/oauth2/token`}</inlineCode>{`, `}<inlineCode parentName="td">{`/users/{user_id}`}</inlineCode>{`, `}<inlineCode parentName="td">{`/users/{user_id}/transactions`}</inlineCode>{`)`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`FDX endpoint set: `}<inlineCode parentName="td">{`/customers/current`}</inlineCode>{`, `}<inlineCode parentName="td">{`/accounts`}</inlineCode>{`, `}<inlineCode parentName="td">{`/accounts/{accountId}`}</inlineCode>{`, `}<inlineCode parentName="td">{`/accounts/{accountId}/contact`}</inlineCode>{`, `}<inlineCode parentName="td">{`/accounts/{accountId}/payment-networks`}</inlineCode>{`, `}<inlineCode parentName="td">{`/accounts/{accountId}/statements`}</inlineCode>{`, `}<inlineCode parentName="td">{`/accounts/{accountId}/asset-transfer-networks`}</inlineCode>{`, `}<inlineCode parentName="td">{`/accounts/{accountId}/transactions`}</inlineCode></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Auth model`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`OAuth 2.0, plus a legacy direct-credential/MFA API Plaid can orchestrate`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`OAuth 2.0 with OIDC strongly recommended; no Plaid-orchestrated MFA API`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Discovery`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Fixed URLs you give Plaid directly; OIDC discovery and JWKS not supported`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`.well-known/openid-configuration`}</inlineCode>{` discovery, with `}<inlineCode parentName="td">{`jwks_uri`}</inlineCode>{` for ID token verification`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Error model`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`HTTP status + a small set of error response shapes (`}<inlineCode parentName="td">{`BasicErrorResponse`}</inlineCode>{`, `}<inlineCode parentName="td">{`AuthenticationError`}</inlineCode>{`, `}<inlineCode parentName="td">{`InstitutionError`}</inlineCode>{`)`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`FDX error entity (`}<inlineCode parentName="td">{`code`}</inlineCode>{`, `}<inlineCode parentName="td">{`message`}</inlineCode>{`, `}<inlineCode parentName="td">{`debugMessage`}</inlineCode>{`) with a persistent numeric `}<inlineCode parentName="td">{`code`}</inlineCode>{`, plus a formal token-endpoint error contract (RFC 6749 §5.2)`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Tooling`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Manual, via your Solutions Engineer`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Data Partner Dashboard, self-serve Validator, Permissions Manager, App Directory`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Support`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Contact your Solutions Engineer directly`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Self-serve Dashboard tickets, routed to a dedicated Data Partner Support Engineering (DPSE) team`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Consent visibility`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`None`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Permissions Manager, Data Transparency Messaging (1033)`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Returning user experience`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Enabled automatically via device/browser fingerprinting`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Enabled automatically; consumers can additionally opt in with phone verification`}</td>
            </tr>
          </tbody>
        </table>
      </Layout.FullWidth>
      <h2 {...{
        "id": "endpoint-mapping"
      }}>{`Endpoint mapping`}</h2>
      <p>{`Plaid Exchange collapsed identity, account, and transaction data into two endpoints. Core Exchange splits the same data across purpose-specific FDX endpoints:`}</p>
      <Layout.FullWidth>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Plaid Exchange`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Core Exchange`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Notes`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/plaid-exchange/docs/reference/aggregation#get-account-and-identity-endpoint"
                }}><inlineCode parentName="a">{`GET /users/{user_id}`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/reference/6.4#accounts"
                }}><inlineCode parentName="a">{`GET /accounts`}</inlineCode></a>{`, `}<a parentName="td" {...{
                  "href": "/core-exchange/docs/reference/6.4#accountsaccountid"
                }}><inlineCode parentName="a">{`GET /accounts/{accountId}`}</inlineCode></a>{`, `}<a parentName="td" {...{
                  "href": "/core-exchange/docs/reference/6.4#accountsaccountidcontact"
                }}><inlineCode parentName="a">{`GET /accounts/{accountId}/contact`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Account list, account detail, and contact information are now separate calls instead of one combined `}<inlineCode parentName="td">{`UserAccountInfoResponse`}</inlineCode>{`.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/plaid-exchange/docs/reference/aggregation#get-transactions-endpoint"
                }}><inlineCode parentName="a">{`GET /users/{user_id}/transactions`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/reference/6.4#accountsaccountidtransactions"
                }}><inlineCode parentName="a">{`GET /accounts/{accountId}/transactions`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Scoped per account rather than across all of a user's accounts in one call.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><em parentName="td">{`(no equivalent; data lived on transfer code models embedded in the account)`}</em></td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/reference/6.4#accountsaccountidpayment-networks"
                }}><inlineCode parentName="a">{`GET /accounts/{accountId}/payment-networks`}</inlineCode></a>{`, `}<a parentName="td" {...{
                  "href": "/core-exchange/docs/reference/6.4#accountsaccountidasset-transfer-networks"
                }}><inlineCode parentName="a">{`GET /accounts/{accountId}/asset-transfer-networks`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`PX represented ACH/EFT/IBAN/card and ACATS transfer identifiers as nested objects (`}<inlineCode parentName="td">{`AchTransferCode`}</inlineCode>{`, `}<inlineCode parentName="td">{`EftTransferCode`}</inlineCode>{`, `}<inlineCode parentName="td">{`AcatsTransferCode`}</inlineCode>{`, and so on) on the account. CX exposes the same kind of identifiers through dedicated endpoints.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><em parentName="td">{`(no equivalent)`}</em></td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/reference/6.4#accountsaccountidstatements"
                }}><inlineCode parentName="a">{`GET /accounts/{accountId}/statements`}</inlineCode></a>{`, `}<a parentName="td" {...{
                  "href": "/core-exchange/docs/reference/6.4#accountsaccountidstatementsstatementid"
                }}><inlineCode parentName="a">{`GET /accounts/{accountId}/statements/{statementId}`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Account statements are a new capability; PX had a `}<inlineCode parentName="td">{`DepositoryAccountStatement`}</inlineCode>{`/`}<inlineCode parentName="td">{`LoanStatement`}</inlineCode>{`/`}<inlineCode parentName="td">{`CreditCardStatement`}</inlineCode>{` model but no dedicated retrieval endpoint.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`id_token.sub`}</inlineCode>{`, `}<inlineCode parentName="td">{`user_id`}</inlineCode>{`, or `}<inlineCode parentName="td">{`GET /customer/current`}</inlineCode>{` (singular; see `}<a parentName="td" {...{
                  "href": "/plaid-exchange/docs/authentication#token-flow"
                }}>{`Token Flow`}</a>{`)`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`sub`}</inlineCode>{` claim, `}<inlineCode parentName="td">{`userinfo_endpoint`}</inlineCode>{`, or `}<a parentName="td" {...{
                  "href": "/core-exchange/docs/reference/6.4#customerscurrent"
                }}><inlineCode parentName="a">{`GET /customers/current`}</inlineCode></a>{` (plural)`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The consistency-key fallback endpoint's path changed from singular to plural. Confirm which one your OAuth server is configured to serve before you cut clients over.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/plaid-exchange/docs/authentication#alternative-auth-methods"
                }}><inlineCode parentName="a">{`POST /users/auth_token`}</inlineCode>{`, `}<inlineCode parentName="a">{`POST /users/{user_id}/sendOtp`}</inlineCode>{`, `}<inlineCode parentName="a">{`POST /users/{user_id}/2fa`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}><em parentName="td">{`(no equivalent)`}</em></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`See `}<a parentName="td" {...{
                  "href": "#authentication-changes"
                }}>{`Authentication changes`}</a>{` below.`}</td>
            </tr>
          </tbody>
        </table>
      </Layout.FullWidth>
      <h2 {...{
        "id": "authentication-changes"
      }}>{`Authentication changes`}</h2>
      <p>{`The overall shape carries over: an OAuth 2.0 authorization code grant, with Plaid redirecting the user to your `}<inlineCode parentName="p">{`authorization_endpoint`}</inlineCode>{` and later exchanging a code at your `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{`. A handful of specifics change:`}</p>
      <h3 {...{
        "id": "no-more-plaid-orchestrated-mfa"
      }}>{`No more Plaid-orchestrated MFA`}</h3>
      <p>{`Plaid Exchange defined a deprecated direct-credential flow (`}<inlineCode parentName="p">{`POST /users/auth_token`}</inlineCode>{`) with Plaid-visible multi-factor escalation: `}<inlineCode parentName="p">{`MfaOtpEscalationChallenge`}</inlineCode>{`, `}<inlineCode parentName="p">{`MfaKbaEscalationChallenge`}</inlineCode>{`, and `}<inlineCode parentName="p">{`MfaTotpEscalationChallenge`}</inlineCode>{` responses, followed by `}<inlineCode parentName="p">{`POST /users/{user_id}/sendOtp`}</inlineCode>{` and `}<inlineCode parentName="p">{`POST /users/{user_id}/2fa`}</inlineCode>{`. Core Exchange has no equivalent endpoints. Plaid requires 2FA for every Core Exchange connection, but the entire authentication flow, including any second factor, happens inside your hosted `}<inlineCode parentName="p">{`authorization_endpoint`}</inlineCode>{`, before redirecting back to Plaid with an authorization code.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` if your PX integration relies on the OTP/KBA/TOTP endpoints rather than pure OAuth, move that challenge logic into your own login flow. There is no code-level migration path for these endpoints; they are removed, not renamed.`}</p>
      <h3 {...{
        "id": "oidc-discovery-and-jwks"
      }}>{`OIDC discovery and JWKS`}</h3>
      <p>{`Plaid Exchange did not support OIDC discovery or JWKS; you gave Plaid your `}<inlineCode parentName="p">{`authorization_endpoint`}</inlineCode>{` and `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{` directly. Core Exchange strongly recommends OIDC, discovered via `}<inlineCode parentName="p">{`.well-known/openid-configuration`}</inlineCode>{`, with ID tokens verified against keys published at your `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{`.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` if you're moving to OIDC as part of this migration (recommended, since OIDC supplies the ID token's `}<inlineCode parentName="p">{`sub`}</inlineCode>{`, the identifier the returning user experience relies on), stand up a discovery document and a JWKS endpoint. See `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup#well-known-configuration"
        }}>{`OAuth server setup`}</a>{`. Plain OAuth 2.0 without OIDC is still supported; resolve the identifier via `}<inlineCode parentName="p">{`/customers/current`}</inlineCode>{` instead.`}</p>
      <h3 {...{
        "id": "new-prompt-parameters"
      }}>{`New `}<inlineCode parentName="h3">{`prompt`}</inlineCode>{` parameters`}</h3>
      <p>{`Core Exchange's authorization redirect includes `}<inlineCode parentName="p">{`prompt=login`}</inlineCode>{`, and its token request includes `}<inlineCode parentName="p">{`prompt=consent`}</inlineCode>{`. Plaid Exchange's flow didn't send either.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` treat an unrecognized `}<inlineCode parentName="p">{`prompt`}</inlineCode>{` value as informational rather than rejecting the request, unless you choose to act on it.`}</p>
      <h3 {...{
        "id": "formal-token-endpoint-error-contract"
      }}>{`Formal token-endpoint error contract`}</h3>
      <p>{`Plaid Exchange didn't specify a response body for a failed token request. Core Exchange requires HTTP 400 with a JSON body containing `}<inlineCode parentName="p">{`error`}</inlineCode>{` (from a defined code list: `}<inlineCode parentName="p">{`invalid_request`}</inlineCode>{`, `}<inlineCode parentName="p">{`invalid_client`}</inlineCode>{`, `}<inlineCode parentName="p">{`invalid_grant`}</inlineCode>{`, `}<inlineCode parentName="p">{`unauthorized_client`}</inlineCode>{`, `}<inlineCode parentName="p">{`unsupported_grant_type`}</inlineCode>{`, `}<inlineCode parentName="p">{`invalid_scope`}</inlineCode>{`) and optional `}<inlineCode parentName="p">{`error_description`}</inlineCode>{`/`}<inlineCode parentName="p">{`error_uri`}</inlineCode>{`, per RFC 6749 §5.2. Core Exchange defines a convention for an expired access token: return HTTP 401 with error code `}<inlineCode parentName="p">{`602 not authorized`}</inlineCode>{`. Plaid treats this as an error for that request; it refreshes tokens before the expiration in the `}<inlineCode parentName="p">{`expires_in`}</inlineCode>{` you returned, so don't expire tokens early.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` if your PX token endpoint returns a bare 400/401 with no structured body, add the `}<inlineCode parentName="p">{`error`}</inlineCode>{` field so Plaid can distinguish failure modes.`}</p>
      <h3 {...{
        "id": "what-doesnt-change"
      }}>{`What doesn't change`}</h3>
      <p><strong parentName="p">{`No changes needed:`}</strong>{` the authorization-redirect error vocabulary (`}<inlineCode parentName="p">{`invalid_request`}</inlineCode>{`, `}<inlineCode parentName="p">{`unauthorized_client`}</inlineCode>{`, `}<inlineCode parentName="p">{`access_denied`}</inlineCode>{`, `}<inlineCode parentName="p">{`unsupported_response_type`}</inlineCode>{`, `}<inlineCode parentName="p">{`invalid_scope`}</inlineCode>{`, `}<inlineCode parentName="p">{`server_error`}</inlineCode>{`, `}<inlineCode parentName="p">{`temporarily_unavailable`}</inlineCode>{`) is identical between PX and CX, as is the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` you send authorization codes back to (`}<inlineCode parentName="p">{`https://cdn.plaid.com/link/v2/stable/oauth.html`}</inlineCode>{`) and the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup#credentials"
        }}>{`client ID / client secret model`}</a>{`.`}</p>
      <h2 {...{
        "id": "data-model-changes"
      }}>{`Data model changes`}</h2>
      <p>{`Plaid Exchange used a Plaid-specific schema (`}<inlineCode parentName="p">{`BaseAccount`}</inlineCode>{`, `}<inlineCode parentName="p">{`DepositoryAccount`}</inlineCode>{`, `}<inlineCode parentName="p">{`InvestmentAccount`}</inlineCode>{`, `}<inlineCode parentName="p">{`LoanAccount`}</inlineCode>{`, `}<inlineCode parentName="p">{`CreditCardAccount`}</inlineCode>{`, `}<inlineCode parentName="p">{`BasicIdentity`}</inlineCode>{`, `}<inlineCode parentName="p">{`FullIdentity`}</inlineCode>{`, and so on), with account type/subtype expressed as lowercase strings (for example `}<inlineCode parentName="p">{`depository`}</inlineCode>{` / `}<inlineCode parentName="p">{`checking`}</inlineCode>{`). Core Exchange uses the FDX-aligned schema documented per version in the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/reference"
        }}>{`API reference`}</a>{`. Check the version you're targeting for exact field names, enum values, and discriminators (for example `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{`), and re-map directly from that schema rather than assuming a 1:1 rename from the PX models.`}</p>
      <p>{`Plaid Exchange's `}<inlineCode parentName="p">{`BasicErrorResponse`}</inlineCode>{`/`}<inlineCode parentName="p">{`AuthenticationError`}</inlineCode>{`/`}<inlineCode parentName="p">{`InstitutionError`}</inlineCode>{` distinguish failures mainly by HTTP status. Core Exchange's error entity carries a persistent FDX `}<inlineCode parentName="p">{`code`}</inlineCode>{` (for example `}<inlineCode parentName="p">{`705`}</inlineCode>{` for "account is closed") alongside `}<inlineCode parentName="p">{`message`}</inlineCode>{` and `}<inlineCode parentName="p">{`debugMessage`}</inlineCode>{`, independent of the HTTP status returned. Build your error handling around the FDX `}<inlineCode parentName="p">{`code`}</inlineCode>{`, not just the status code.`}</p>
      <h2 {...{
        "id": "capabilities-not-available-in-plaid-exchange"
      }}>{`Capabilities not available in Plaid Exchange`}</h2>
      <ul>
        <li parentName="ul"><strong parentName="li"><a parentName="strong" {...{
              "href": "/core-exchange/docs/dashboard-overview"
            }}>{`Data Partner Dashboard`}</a>{` and `}<a parentName="strong" {...{
              "href": "/core-exchange/docs/testing"
            }}>{`Validator`}</a>{`:`}</strong>{` self-serve configuration and endpoint-by-endpoint testing.`}</li>
        <li parentName="ul"><strong parentName="li"><a parentName="strong" {...{
              "href": "/core-exchange/docs/consent-management"
            }}>{`Permissions Manager`}</a>{`:`}</strong>{` visibility into authorization records for your customers' connections, including revocation and reauthorization tracking.`}</li>
        <li parentName="ul"><strong parentName="li"><a parentName="strong" {...{
              "href": "/core-exchange/docs/permissions-manager"
            }}>{`App Directory`}</a>{`:`}</strong>{` a catalog of the applications your customers connect to, via `}<a parentName="li" {...{
            "href": "/core-exchange/docs/permissions-manager/app-directory"
          }}><inlineCode parentName="a">{`GET /fdx/recipients`}</inlineCode>{` and `}<inlineCode parentName="a">{`GET /fdx/recipient/<recipient_id>`}</inlineCode></a>{` or the Dashboard, without implementing Dynamic Client Registration.`}</li>
        <li parentName="ul"><strong parentName="li"><a parentName="strong" {...{
              "href": "/core-exchange/docs/user-experience#data-transparency-messaging"
            }}>{`Data Transparency Messaging`}</a>{`:`}</strong>{` Plaid-hosted disclosure of the data types an app is requesting, supporting 1033 compliance.`}</li>
        <li parentName="ul"><strong parentName="li">{`App2App implementation guide:`}</strong>{` deep-link parameters and an `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication/app2app"
          }}>{`implementation checklist`}</a>{`; Plaid Exchange described App2App only at the UX level.`}</li>
      </ul>
      <p>{`None of these require changes to your core data or auth implementation; they are additive once you are live on Core Exchange.`}</p>
      <h2 {...{
        "id": "support-and-logging"
      }}>{`Support and logging`}</h2>
      <p>{`Plaid Exchange support runs through your Solutions Engineer directly, for both routine questions and bug reports. Core Exchange moves this to a `}<a parentName="p" {...{
          "href": "/core-exchange/docs/post-launch#getting-support"
        }}>{`self-serve flow`}</a>{`: file tickets from the Dashboard under `}<strong parentName="p">{`Get Help`}</strong>{` by selecting `}<strong parentName="p">{`Create case`}</strong>{` and choosing `}<strong parentName="p">{`Open finance issues`}</strong>{`, which route to a dedicated Data Partner Support Engineering (DPSE) team. Solutions Engineering may still re-engage for major expansions or architectural changes.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` update any internal runbooks or on-call documentation that name your Solutions Engineer as the support contact to reference the Dashboard ticketing flow instead.`}</p>
      <p>{`Core Exchange introduces a `}<a parentName="p" {...{
          "href": "/core-exchange/docs/post-launch#logging-identifiers-for-troubleshooting"
        }}>{`logging identifier`}</a>{` with no Plaid Exchange equivalent: the `}<inlineCode parentName="p">{`X-Request-ID`}</inlineCode>{` header Plaid sends with requests to your resource server, correlated with the Dashboard's Integration Health view. Keep logging the OAuth `}<inlineCode parentName="p">{`state`}</inlineCode>{` parameter and your user identifier as you did in Plaid Exchange (as `}<inlineCode parentName="p">{`oauth_state_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`FI_UNIQUE_ID`}</inlineCode>{`), and add `}<inlineCode parentName="p">{`X-Request-ID`}</inlineCode>{` alongside them.`}</p>
      <h2 {...{
        "id": "migration-path"
      }}>{`Migration path`}</h2>
      <p>{`Before any cohort work begins, sign up for a `}<a parentName="p" {...{
          "href": "/core-exchange/docs/dashboard-overview"
        }}>{`Data Partner Dashboard`}</a>{` account and complete your institution profile: business and compliance details (legal entity name, address, tax ID, Legal Entity Identifier, industry, and licensing or registration such as FDIC, NCUA, or SIPC) and technical contacts. Plaid Exchange never required this; provisioning happened directly with your Plaid contact. See `}<a parentName="p" {...{
          "href": "/core-exchange/docs/implementation-checklist#scoping-and-implementation"
        }}>{`Implementation checklist`}</a>{` for the full list.`}</p>
      <p>{`If your institution already has live PX traffic, Plaid coordinates a `}<a parentName="p" {...{
          "href": "/core-exchange/docs/implementation-checklist#pilot-and-migrate"
        }}>{`phased migration`}</a>{` rather than a single cutover, similar to a fresh Core Exchange launch:`}</p>
      <ol>
        <li parentName="ol"><strong parentName="li">{`Pilot cohort:`}</strong>{` new connections move to Core Exchange in production for a small cohort while Plaid Exchange keeps serving existing traffic.`}</li>
        <li parentName="ol"><strong parentName="li">{`Remaining cohorts:`}</strong>{` expand to the rest of your users, cohort by cohort.`}</li>
        <li parentName="ol"><strong parentName="li">{`Existing user migration:`}</strong>{` once integration health is validated, Plaid migrates existing Items from Plaid Exchange over to the Core Exchange OAuth + API connection. This phase runs over approximately 90 days (Plaid Exchange's own migration guidance cites roughly 120 days for the equivalent phase), and in select cases `}<a parentName="li" {...{
            "href": "/core-exchange/docs/migrating-from-plaid-exchange/backend-token-exchange"
          }}>{`backend token exchange`}</a>{` can avoid requiring users to reauthenticate.`}</li>
      </ol>
      <p>{`Actual timelines depend on institutional health validation and issue resolution; confirm them with your Plaid contact.`}</p>
      <h2 {...{
        "id": "after-migrating"
      }}>{`After migrating`}</h2>
      <ul>
        <li parentName="ul">{`Update your IP allowlist: keep every IP already allowlisted for Plaid Exchange, and add Core Exchange's ranges, `}<inlineCode parentName="li">{`162.120.88.0/22`}</inlineCode>{` and `}<inlineCode parentName="li">{`2602:F74B::/40`}</inlineCode>{`. See `}<a parentName="li" {...{
            "href": "/core-exchange/docs/security#ip-allowlist"
          }}>{`IP allowlisting`}</a>{`.`}</li>
        <li parentName="ul">{`Run your integration through the `}<a parentName="li" {...{
            "href": "/core-exchange/docs/testing"
          }}>{`Validator`}</a>{` before requesting `}<a parentName="li" {...{
            "href": "/core-exchange/docs/production-access"
          }}>{`production access`}</a>{` on Core Exchange.`}</li>
        <li parentName="ul">{`Decommission the Plaid Exchange OTP/KBA/TOTP endpoints only once Plaid has moved all of your institution's traffic, including any pilot cohorts, over to Core Exchange.`}</li>
        <li parentName="ul">{`Point any internal documentation, client libraries, or generated code at the Core Exchange `}<a parentName="li" {...{
            "href": "/core-exchange/docs/reference"
          }}>{`API reference`}</a>{` instead of the Plaid Exchange `}<a parentName="li" {...{
            "href": "/plaid-exchange/docs/reference/data-definitions"
          }}>{`data definitions`}</a>{`.`}</li>
      </ul>
      <h2 {...{
        "id": "additional-resources"
      }}>{`Additional resources`}</h2>
      <p>{`These aren't specific to migrating from Plaid Exchange, but cover the rest of the Core Exchange onboarding journey:`}</p>
      <ul>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/onboarding-guide"
          }}>{`Onboarding guide`}</a>{`: the end-to-end integration journey, from setup through post-launch.`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/dashboard-overview"
          }}>{`Dashboard overview`}</a>{`: configuring your institution profile, environments, and branding in the Data Partner Dashboard.`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/implementation-checklist"
          }}>{`Implementation checklist`}</a>{`: every step required during build, testing, and go-live.`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/testing"
          }}>{`Testing & validation`}</a>{`: using the Validator to test authentication and data endpoints.`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/production-access"
          }}>{`Production access`}</a>{`: requesting production access and running stealth testing.`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/post-launch"
          }}>{`Post-launch operations`}</a>{`: support, monitoring, target metrics, and logging identifiers.`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/troubleshooting"
          }}>{`Troubleshooting`}</a>{`: debugging common OAuth and data-flow issues.`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/security"
          }}>{`Security best practices`}</a>{`: IP allowlisting, mTLS, and PKCE.`}</li>
      </ul>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "why-migrate",
        level: 2,
        title: "Why migrate",
      },
{
        id: "whats-different-at-a-glance",
        level: 2,
        title: "What's different, at a glance",
      },
{
        id: "endpoint-mapping",
        level: 2,
        title: "Endpoint mapping",
      },
{
        id: "authentication-changes",
        level: 2,
        title: "Authentication changes",
      },
{
        id: "no-more-plaid-orchestrated-mfa",
        level: 3,
        title: "No more Plaid-orchestrated MFA",
      },
{
        id: "oidc-discovery-and-jwks",
        level: 3,
        title: "OIDC discovery and JWKS",
      },
{
        id: "new-prompt-parameters",
        level: 3,
        title: "New prompt parameters",
      },
{
        id: "formal-token-endpoint-error-contract",
        level: 3,
        title: "Formal token-endpoint error contract",
      },
{
        id: "what-doesnt-change",
        level: 3,
        title: "What doesn't change",
      },
{
        id: "data-model-changes",
        level: 2,
        title: "Data model changes",
      },
{
        id: "capabilities-not-available-in-plaid-exchange",
        level: 2,
        title: "Capabilities not available in Plaid Exchange",
      },
{
        id: "support-and-logging",
        level: 2,
        title: "Support and logging",
      },
{
        id: "migration-path",
        level: 2,
        title: "Migration path",
      },
{
        id: "after-migrating",
        level: 2,
        title: "After migrating",
      },
{
        id: "additional-resources",
        level: 2,
        title: "Additional resources",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
